import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Asterisk } from "lucide-react";
import { LOGO_URL } from "../../lib/api";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero({ content }) {
  const hero = content?.hero || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cubesY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const cubesRotate = useTransform(scrollYProgress, [0, 1], [0, 8]);
  const lines = hero.titleLines || [];

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center pt-28 pb-16 overflow-hidden"
    >
      {/* background grid lines */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "80px 80px" }}
      />
      {/* floating logo */}
      <motion.div
        style={{ y: cubesY, rotate: cubesRotate }}
        className="absolute top-24 right-6 xl:right-12 hidden lg:block pointer-events-none z-0"
      >
        <motion.img
          src={LOGO_URL}
          alt=""
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="floaty w-[300px] xl:w-[360px] h-auto opacity-95 drop-shadow-[0_24px_50px_rgba(0,0,0,0.6)]"
        />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <Asterisk className="text-peca-yellow" size={18} />
          <span className="label-mono text-peca-grey max-w-xs md:max-w-none">
            {hero.eyebrow}
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.88] text-peca-cream text-4xl sm:text-6xl lg:text-[8rem] break-words">
          {lines.map((line, i) => {
            const isHighlight = hero.highlight
              ? line === hero.highlight
              : i === lines.length - 1;
            return (
              <span key={i} className="reveal-mask">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block"
                  style={{ color: isHighlight ? "#E6FF00" : undefined }}
                >
                  {line}
                </motion.span>
              </span>
            );
          })}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-10 max-w-xl"
        >
          <p className="text-peca-grey text-base md:text-lg leading-relaxed font-body">
            {hero.subtitle}
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="#contacto"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-peca-yellow text-peca-black font-display font-bold uppercase text-sm tracking-wide px-7 py-4 hard-shadow-black hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-transform duration-200"
            >
              {hero.ctaPrimary}
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-200" />
            </a>
            <a
              href="#servicos"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border-2 border-peca-line text-peca-cream font-display font-bold uppercase text-sm tracking-wide px-7 py-4 hover:border-peca-yellow hover:text-peca-yellow transition-colors duration-200"
            >
              {hero.ctaSecondary}
            </a>
          </div>
        </motion.div>

        {/* stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-16 md:mt-24 grid grid-cols-3 gap-x-6 gap-y-4 max-w-2xl border-t border-peca-line pt-8"
        >
          {(hero.stats || []).map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="font-display font-extrabold text-3xl md:text-5xl text-peca-yellow">{s.value}</div>
              <div className="label-mono text-peca-grey mt-2 leading-tight">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
